import type { ReactNode } from 'react';

/**
 * An ambient glow behind the brand mark.
 *
 * Dark-first brand languages (Linear, Vercel, Raycast) do not sit flat on their canvas — a soft
 * bloom of the accent colour sits behind the mark and the primary action, which is what stops a
 * near-black screen reading as an empty void. Without it a dark app looks switched off.
 *
 * It is a radial gradient rather than a `box-shadow` because a shadow follows the element's box: on
 * a rounded square you get a visible rectangle of light. A radial gradient stays circular and
 * unbounded, which is what "glow" actually looks like.
 *
 * Colour comes from `--brand-primary`, so the glow is part of the brand rather than a fixed effect,
 * and it disappears correctly on a light brand where it would only add haze.
 */
export function BrandGlow({
	children,
	size = 260,
	intensity = 0.28,
	className,
}: {
	children: ReactNode;
	/** Diameter of the bloom in px. Should be several times the mark it sits behind. */
	size?: number;
	/** Peak alpha at the centre. Past ~0.35 it stops reading as light and starts reading as fog. */
	intensity?: number;
	className?: string;
}) {
	return (
		<div className={`relative isolate grid place-items-center ${className ?? ''}`}>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute -z-10 rounded-full"
				style={{
					width: size,
					height: size,
					background: `radial-gradient(circle, color-mix(in srgb, var(--brand-primary) ${Math.round(
						intensity * 100,
					)}%, transparent) 0%, transparent 68%)`,
					filter: 'blur(18px)',
				}}
			/>
			{children}
		</div>
	);
}
